import { NewButton, NewButtonVariant } from "@visiblethread/react-components";
import { Plus } from "lucide-react";
import { forwardRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { insertNewSection } from "slices/proposalOutline/actions/outlineActions";
import {
  selectIsEditing,
  selectProposalOutlineSections,
  selectSelectedIndex,
} from "slices/proposalOutline/selectors";

import classes from "./OutlineActions.module.scss";

const OutlineActions = forwardRef((_, excelTableRef) => {
  const sections = useSelector(selectProposalOutlineSections);
  const isEditing = useSelector(selectIsEditing);
  const selectedIndex = useSelector(selectSelectedIndex);
  const dispatch = useDispatch();

  const summary = useMemo(() => {
    if (!sections?.length) {
      return { sections: "0", levels: "-" };
    }

    let maxLevel = 0;
    for (const section of sections) {
      maxLevel = Math.max(section.level, maxLevel);
    }

    return {
      sections: sections.length,
      levels: maxLevel,
    };
  }, [sections]);

  const getTableRows = () => {
    const { table } = excelTableRef.current || {};

    if (!table?.getRowModel) {
      return [];
    }

    return table.getRowModel()?.rows || [] || {};
  };

  const onClickAddNew = async () => {
    if (!excelTableRef) {
      return;
    }

    const index = Math.max(0, selectedIndex);
    const tableRows = getTableRows();

    const selectedRow = tableRows[index];
    const targetLevel = selectedRow?.original?.level || 1;
    // Use the level / depth from the originating Row

    const newSection = dispatch(
      insertNewSection({ index, level: targetLevel })
    );

    if (excelTableRef.current) {
      excelTableRef.current.focusByCellId(newSection?.id, "title");
    }
  };

  return (
    <div className={classes["outline_actions"]}>
      <div className={classes["outline_actions__summary"]}>
        <span data-testid="sections-length">
          Sections (<strong>{summary.sections}</strong>)
        </span>
        <span data-testid="max-level">
          Levels (<strong>{summary.levels}</strong>)
        </span>
      </div>

      {isEditing && (
        <NewButton
          classes={classes["outline_actions__add_new"]}
          onClick={onClickAddNew}
          variant={NewButtonVariant.l1_5}
          visible={isEditing}
          leftIcon={<Plus size={16} />}
        >
          New Section
        </NewButton>
      )}
    </div>
  );
});

OutlineActions.displayName = "OutlineActions";

export default OutlineActions;
